// SSE streaming client for POST /chat/stream/sse.
//
// Native `EventSource` only supports GET requests, and this endpoint needs a
// JSON body (the message history + radius), so we drive the same
// `text/event-stream` protocol manually with `fetch` + a streamed
// `ReadableStream` reader. Each `data: <json>\n\n` frame is decoded and
// handed to `onEvent` as soon as it arrives — this is what makes the UI
// render tokens/data immediately instead of waiting for the full reply.

import { config } from "../config";
import type { ChatMessage, ChatStreamEvent } from "../types";

export class ChatStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

interface StreamChatOptions {
  messages: ChatMessage[];
  radiusKm: number;
  onEvent: (event: ChatStreamEvent) => void;
  signal?: AbortSignal;
}

export async function streamChat({ messages, radiusKm, onEvent, signal }: StreamChatOptions): Promise<void> {
  let res: Response;

  try {
    res = await fetch(`${config.apiBase}/chat/stream/sse`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": config.apiSecretKey,
      },
      body: JSON.stringify({ messages, radius_km: radiusKm }),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ChatStreamError("The server is temporarily unavailable.");
  }

  if (res.status === 401) throw new ChatStreamError("Authentication failed.", 401);
  if (res.status === 429) throw new ChatStreamError("Too many requests. Please wait.", 429);
  if (!res.ok) throw new ChatStreamError("The request could not be processed.", res.status);

  if (!res.body) {
    throw new ChatStreamError("Streaming is not supported by this browser.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line ("\n\n").
    let separatorIndex: number;
    while ((separatorIndex = buffer.indexOf("\n\n")) !== -1) {
      const rawFrame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      const dataLines = rawFrame
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart());

      if (dataLines.length === 0) continue;

      const payload = dataLines.join("\n");

      try {
        const event = JSON.parse(payload) as ChatStreamEvent;
        onEvent(event);
      } catch {
        // Ignore malformed frames rather than killing the whole stream.
        continue;
      }
    }
  }
}
